import React, { useMemo, useState } from 'react';
import { DNABox, DNASpinner, DNAText, Iffy } from '@alucio/lux-ui';
import { DocumentORM } from 'src/types/types';
import useFixedHeader from '@alucio/lux-ui/src/hooks/useFixedLayout';
import { useQuickFilters } from 'src/components/QuickFilters/QuickFiltersProvider';
import FilterButton, { VariantOptions } from 'src/components/QuickFilters/FilterButton';
import { DNADocumentFiltersChips } from 'src/components/DNA/Document/DNADocumentFilters';
import { TitleBar } from 'src/components/DNA';
import { useLibraryDisplayMode } from './DocumentSelectManager';
import DocumentTitleSearchInput from 'src/components/DocumentTitleSearchInput';

interface HeaderProps {
  searchText?: string;
  showLoader: boolean;
  documentsCount: number;
  filtersVisible: boolean;
  setFiltersVisible: React.Dispatch<React.SetStateAction<boolean>>;
  documents: DocumentORM[];
  titleSearchText: string;
  setTitleSearchText: React.Dispatch<React.SetStateAction<string>>;
}

// Component that handles the header content with minimal prop drilling
const HeaderContent: React.FC<{ searchText?: string; showLoader: boolean; documentsCount: number }> = ({
  searchText,
  showLoader,
  documentsCount,
}) => {
  const { displayMode, toggleDisplayMode } = useLibraryDisplayMode();
  const isSearch = !!searchText?.length;

  const titleContent = useMemo(
    () =>
      showLoader && isSearch ? (
        <DNABox appearance="row" alignY="center" spacing="sm">
          <DNASpinner size="small" />
          <DNAText h5>Loading results...</DNAText>
        </DNABox>
      ) : null,
    [showLoader, isSearch],
  );

  if (showLoader && isSearch) {
    return (
      <DNABox fill style={{ paddingTop: 16, paddingBottom: 24 }}>
        {titleContent}
      </DNABox>
    )
  }

  if (showLoader) {
    return (
      <TitleBar title="Loading..." testID="page-title" />
    )
  }

  const title = isSearch ? `Search results for "${searchText}"` : 'Library';
  const description = isSearch
    ? showLoader
      ? ''
      : `${documentsCount} result${documentsCount === 1 ? '' : 's'}`
    : 'Browse and filter for content';

  return (
    <TitleBar
      title={title}
      testID="page-title"
      description={description}
      showToggleView={!isSearch}
      toggleView={{
        activeView: displayMode,
        onToggle: toggleDisplayMode,
      }}
      badge={isSearch && showLoader ? titleContent : undefined}
    />
  );
};

const Header: React.FC<HeaderProps> = (props) => {
  const {
    searchText,
    showLoader,
    documentsCount,
    filtersVisible,
    setFiltersVisible,
    documents,
    titleSearchText,
    setTitleSearchText,
  } = props;
  const [filtersMinimumWidth, setFiltersMinimumWidth] = useState<number>();
  const { handleClear: handleClearQuickFilters } = useQuickFilters();
  const fixedHeaderWidth = useFixedHeader();
  const isSearch = !!searchText?.length;

  return (
    <DNABox fill appearance="col" spacing="md" style={{ maxWidth: fixedHeaderWidth }}>
      {/* HEADER ROW 1 */}
      <HeaderContent
        searchText={searchText}
        showLoader={showLoader}
        documentsCount={documentsCount}
      />

      <Iffy is={!showLoader}>
        {/* HEADER ROW 2 */}
        <DNABox
          fill
          wrap="start"
          alignY="center"
          spacing="md"
          childStyle={[1, {
            flexGrow: 1,
            flexShrink: 1,
            flexBasis: filtersMinimumWidth ?? 'auto',
            maxWidth: '100%',
          }]}
          style={{ rowGap: 16 }}
        >
          <DNABox alignY="center" spacing="md">
            <DNABox testID="filters-header-button">
              <FilterButton
                setFiltersVisible={setFiltersVisible}
                filtersVisible={filtersVisible}
                variant={VariantOptions.library}
              />
            </DNABox>
            {!isSearch && (
              <DocumentTitleSearchInput
                value={titleSearchText}
                onChangeText={setTitleSearchText}
              />
            )}
          </DNABox>
          <DNADocumentFiltersChips
            documents={documents}
            onMinimumWidthChange={setFiltersMinimumWidth}
            onClearFilters={handleClearQuickFilters}
            isLoading={showLoader}
          />
        </DNABox>
      </Iffy>
    </DNABox>
  )
}

export default Header;
